import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, apiErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useBrand } from "../../lib/useBrand";
import { AuthModeTabs, MagicLinkForm } from "../../components/MagicLinkForm";
import { SESSION_REASONS } from "../../lib/useSessionGuard";
import { PasswordField } from "../../components/PasswordField";

export function EmployeeAuthShell({ title, subtitle, children, footer }) {
  const brand = useBrand();
  return (
    <div className="emp-theme min-h-screen flex flex-col items-center justify-center p-4 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="w-full max-w-md bg-white rounded-[2rem] shadow-xl shadow-blue-900/5 p-8 sm:p-10" data-testid="employee-auth-card">
        <div className="flex flex-col items-center text-center mb-8">
          {(brand.login_logo || brand.logo) ? <img src={brand.login_logo || brand.logo} alt="" className="h-14 w-14 rounded-2xl object-contain bg-slate-50 p-2" /> : <span className="h-14 w-14 rounded-2xl bg-blue-600 text-white font-extrabold text-2xl grid place-items-center emp-display">P</span>}
          <div className="font-extrabold text-xl text-slate-900 mt-3 emp-display">{brand.name || "PostWage"}</div>
          <div className="emp-label mt-1">Employee portal</div>
        </div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="text-base text-slate-500 mt-2 leading-relaxed">{subtitle}</p>}
        <div className="mt-6">{children}</div>
        {footer && <div className="mt-8 text-sm text-slate-500 text-center leading-relaxed">{footer}</div>}
      </div>
    </div>
  );
}

export const Field = ({ label, children }) => (<label className="block mb-4"><span className="emp-label block mb-1.5 ml-1">{label}</span>{children}</label>);
export const inputCls = "emp-input";

function ForgotForm({ email, setEmail, onDone }) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch { setSent(true); } finally { setBusy(false); }
  };
  if (sent) return <div data-testid="employee-forgot-sent" className="text-sm text-slate-600 bg-emerald-50 rounded-2xl px-4 py-4 leading-relaxed"><strong>Check your inbox.</strong> If <span className="font-mono">{email}</span> has an account, a password reset link is on its way (valid 1 hour).<button type="button" onClick={onDone} className="block mt-3 text-blue-600 underline underline-offset-2">Back to sign in</button></div>;
  return (
    <form onSubmit={submit} data-testid="employee-forgot-form">
      <p className="text-sm text-slate-500 mb-4">Enter your work email and we'll send a secure link to choose a new password.</p>
      <Field label="Work email"><input data-testid="employee-forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} /></Field>
      <button data-testid="employee-forgot-submit" disabled={busy} className="emp-btn w-full">{busy ? "Sending…" : "Send reset link"}</button>
      <button type="button" data-testid="employee-forgot-cancel" onClick={onDone} className="block w-full text-center text-sm text-slate-500 mt-4 hover:text-blue-600">Back to sign in</button>
    </form>
  );
}

export default function EmployeeLogin() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(params.get("mode") === "magic" ? "magic" : "password");
  const [forgot, setForgot] = useState(params.get("forgot") === "1");
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const reason = SESSION_REASONS[params.get("reason")];

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.role !== "employee") { setErr("This sign-in is for employees. Staff and client users sign in at the main page."); await api.post("/auth/logout").catch(() => {}); return; }
      setUser(data); navigate("/me", { replace: true });
    } catch (e2) { setErr(apiErr(e2)); } finally { setBusy(false); }
  };

  return (
    <EmployeeAuthShell title={forgot ? "Reset password" : "Sign in"} subtitle={forgot ? "" : "View your pay stubs, T4s, time and profile."}
      footer={<>New here? <Link to="/employee/register" data-testid="employee-register-link" className="emp-link">Create your login</Link><br /><Link to="/login" className="text-slate-400 hover:text-slate-600">Staff / client sign-in</Link></>}>
      {reason && !forgot && <p data-testid="employee-session-reason" className="text-sm text-slate-600 bg-amber-50 rounded-2xl px-4 py-3 mb-4">{reason}</p>}
      {forgot ? <ForgotForm email={email} setEmail={setEmail} onDone={() => setForgot(false)} /> : (
        <>
          <AuthModeTabs mode={mode} setMode={setMode} variant="employee" testPrefix="employee-auth-mode" />
          {mode === "magic" ? <MagicLinkForm email={email} setEmail={setEmail} variant="employee" testPrefix="employee-magic" /> : (
            <form onSubmit={submit} data-testid="employee-login-form">
              <Field label="Work email"><input data-testid="employee-login-email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} /></Field>
              <Field label="Password"><PasswordField testId="employee-login-password" variant="employee" required value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
              {err && <p data-testid="employee-login-error" className="text-sm font-medium text-rose-600 bg-rose-50 rounded-2xl px-4 py-3 mb-4">{err}</p>}
              <button data-testid="employee-login-submit" disabled={busy} className="emp-btn w-full">{busy ? "Signing in…" : "Sign in"}</button>
              <button type="button" data-testid="employee-forgot-link" onClick={() => setForgot(true)} className="block w-full text-center text-sm text-slate-500 mt-5 hover:text-blue-600 font-medium">Forgot password?</button>
            </form>
          )}
        </>
      )}
    </EmployeeAuthShell>
  );
}
